import { lazy, Suspense, useEffect, useState } from 'react';
import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalFooter,
  ModalCloseButton,
  Button,
  VStack,
  HStack,
  Text,
  Box,
  Image,
  IconButton,
  Spinner,
  useToast,
} from '@chakra-ui/react';
import { Camera, Video, X, Upload } from 'lucide-react';
import { useCreateStory } from '@/api/mutations/useSocialMutations';
import { useCloudinaryUpload } from '@/hooks/useCloudinaryUpload';
import type { StoryMediaType, CreateStoryRequest } from '@/types/social';
import type { TextOverlay } from './overlay/types';
import { OverlayEditorErrorBoundary } from './overlay/OverlayEditorErrorBoundary';

/**
 * Lazy-load the text overlay editor so its drag-and-touch logic is only
 * bundled when the user actually activates the "Add Text" step. This keeps
 * the initial modal load unaffected (NF6: ≤15 KB gzipped for the editor chunk).
 */
const StoryTextOverlayEditor = lazy(
  () => import('./overlay/StoryTextOverlayEditor')
);

/**
 * Steps in the story creation flow.
 * - upload: choose and upload media
 * - overlay: add text overlays (image stories only)
 * - publish: review and share
 */
type ModalStep = 'upload' | 'overlay' | 'publish';

interface StoryUploadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
}

export function StoryUploadModal({ isOpen, onClose, onSuccess }: StoryUploadModalProps) {
  const [step, setStep] = useState<ModalStep>('upload');
  const [mediaUrl, setMediaUrl] = useState<string | null>(null);
  const [thumbnailUrl, setThumbnailUrl] = useState<string | null>(null);
  const [mediaType, setMediaType] = useState<StoryMediaType>('image');
  const [duration, setDuration] = useState<number | null>(null);
  /** Text overlays accumulated in the editor step */
  const [textOverlays, setTextOverlays] = useState<TextOverlay[]>([]);

  const toast = useToast();
  const createStory = useCreateStory();

  // Use centralized Cloudinary upload hook with direct mode for stories
  const { openFilePicker, isUploading, pendingFile, confirmUpload } = useCloudinaryUpload({
    mode: 'direct',
    resourceType: 'auto', // Support both images and videos
    minVideoDuration: 15,
    maxVideoDuration: 60,
    onSuccess: (result) => {
      setMediaUrl(result.url);
      setMediaType(result.mediaType);
      if (result.thumbnailUrl) {
        setThumbnailUrl(result.thumbnailUrl);
      }
      if (result.duration) {
        setDuration(result.duration);
      }
      // After upload, advance to overlay step for images; skip to publish for video
      setStep(result.mediaType === 'image' ? 'overlay' : 'publish');
    }
  });

  // Direct mode's two-step flow only stages the file selection —
  // openFilePicker() does not upload by itself; confirmUpload() must be
  // called explicitly once a file is staged. Without this, confirmUpload()
  // never runs and onSuccess (and therefore the upload) never fires.
  useEffect(() => {
    if (pendingFile) {
      void confirmUpload();
    }
  }, [pendingFile, confirmUpload]);

  const handleSubmit = async () => {
    if (!mediaUrl) {
      toast({
        title: 'Please upload media',
        status: 'warning',
        duration: 2000,
      });
      return;
    }

    try {
      const storyData: CreateStoryRequest = {
        media_url: mediaUrl,
        media_type: mediaType,
        // Caption step removed — send empty string to satisfy API schema
        caption: '',
      };

      if (thumbnailUrl) {
        storyData.thumbnail_url = thumbnailUrl;
      }

      if (duration) {
        storyData.duration = duration;
      }

      // Attach text overlays as metadata when present
      if (textOverlays.length > 0) {
        storyData.textOverlays = textOverlays;
      }

      await createStory.mutateAsync(storyData);

      toast({
        title: 'Story created!',
        status: 'success',
        duration: 2000,
      });

      onSuccess?.();
      handleClose();
    } catch (error) {
      console.error('Failed to create story:', error);
      toast({
        title: 'Failed to create story',
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleClose = () => {
    setStep('upload');
    setMediaUrl(null);
    setThumbnailUrl(null);
    setMediaType('image');
    setDuration(null);
    setTextOverlays([]);
    onClose();
  };

  const clearMedia = () => {
    setMediaUrl(null);
    setThumbnailUrl(null);
    setDuration(null);
    setStep('upload');
    setTextOverlays([]);
  };

  /** Called when user taps "Add Text / Edit Text" from the publish step */
  const handleGoToOverlay = () => {
    if (mediaType === 'image') {
      setStep('overlay');
    }
  };

  // Overlay step occupies the full modal body without the standard header/footer
  if (step === 'overlay' && mediaUrl) {
    return (
      <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
        <ModalOverlay bg="blackAlpha.900" />
        <ModalContent bg="black" borderRadius="var(--r-card)" mx={4} overflow="hidden">
          <ModalCloseButton color="white" zIndex={30} />
          <OverlayEditorErrorBoundary>
            <Suspense
              fallback={
                <Box
                  h="400px"
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  bg="black"
                >
                  <Spinner color="white" />
                </Box>
              }
            >
              <StoryTextOverlayEditor
                mediaUrl={mediaUrl}
                mediaType={mediaType}
                textOverlays={textOverlays}
                onChange={setTextOverlays}
                onDone={() => setStep('publish')}
              />
            </Suspense>
          </OverlayEditorErrorBoundary>
        </ModalContent>
      </Modal>
    );
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="md" isCentered>
      <ModalOverlay bg="blackAlpha.700" backdropFilter="blur(8px)" />
      <ModalContent bg="var(--card-bg)" borderRadius="var(--r-card)" mx={4}>
        <ModalHeader>
          <HStack spacing={3}>
            <Box bg="pink.100" borderRadius="var(--r-chip)" p={2}>
              <Camera size={20} color="var(--chakra-colors-pink-500)" />
            </Box>
            <VStack align="start" spacing={0}>
              <Text fontSize="lg" fontWeight="bold" color="var(--card-text)">
                Create Story
              </Text>
              <Text fontSize="xs" color="var(--card-text-2)" fontWeight="normal">
                Share a moment with your network
              </Text>
            </VStack>
          </HStack>
        </ModalHeader>
        <ModalCloseButton />

        <ModalBody>
          <VStack spacing={4}>
            {/* Media upload area — shown in upload and publish steps */}
            {!mediaUrl ? (
              <Box
                w="full"
                h="300px"
                border="2px dashed"
                borderColor="var(--color-gray-300)"
                borderRadius="var(--r-inner)"
                display="flex"
                flexDirection="column"
                alignItems="center"
                justifyContent="center"
                cursor="pointer"
                onClick={openFilePicker}
                _hover={{ borderColor: 'var(--color-primary)', bg: 'var(--color-gray-50)' }}
                transition="all 0.2s"
              >
                {isUploading ? (
                  <VStack spacing={2}>
                    <Upload className="animate-pulse" size={48} />
                    <Text color="var(--color-gray-500)">Uploading...</Text>
                  </VStack>
                ) : (
                  <VStack spacing={3}>
                    <HStack spacing={6}>
                      <VStack>
                        <Camera size={32} />
                        <Text fontSize="sm">Photo</Text>
                      </VStack>
                      <VStack>
                        <Video size={32} />
                        <Text fontSize="sm">Video (15-60s)</Text>
                      </VStack>
                    </HStack>
                    <Text color="var(--color-gray-500)" fontSize="sm">
                      Click to upload
                    </Text>
                  </VStack>
                )}
              </Box>
            ) : (
              <Box position="relative" w="full">
                {mediaType === 'video' ? (
                  <Box
                    as="video"
                    src={mediaUrl}
                    w="full"
                    h="300px"
                    objectFit="cover"
                    borderRadius="var(--r-inner)"
                    controls
                  />
                ) : (
                  <Image
                    src={mediaUrl}
                    alt="Story preview"
                    w="full"
                    h="300px"
                    objectFit="cover"
                    borderRadius="var(--r-inner)"
                  />
                )}
                <IconButton
                  aria-label="Remove media"
                  icon={<X size={20} />}
                  position="absolute"
                  top={2}
                  right={2}
                  size="sm"
                  bg="var(--color-error)"
                  color="white"
                  _hover={{ bg: 'var(--color-overlay-70)' }}
                  onClick={clearMedia}
                />
                {/* Edit overlays button — only for image stories on the publish step */}
                {mediaType === 'image' && step === 'publish' && (
                  <Button
                    position="absolute"
                    bottom={2}
                    right={2}
                    size="xs"
                    bg="var(--color-overlay-70)"
                    color="white"
                    _hover={{ bg: 'var(--color-overlay-80)' }}
                    onClick={handleGoToOverlay}
                  >
                    {textOverlays.length > 0 ? `Edit Text (${textOverlays.length})` : 'Add Text'}
                  </Button>
                )}
              </Box>
            )}

            {/* Publish step info — text overlays are photo-only */}
            {step === 'publish' && mediaType === 'video' && (
              <Text fontSize="xs" color="var(--card-text-2)" textAlign="center" px={2}>
                Text overlays are available for photos only.
              </Text>
            )}
          </VStack>
        </ModalBody>

        <ModalFooter borderTop="1px solid" borderColor="var(--card-divider)" pt={4}>
          <HStack spacing={3} w="100%">
            <Button variant="ghost" flex={1} onClick={handleClose}>
              Cancel
            </Button>
            {step === 'publish' ? (
              <Button
                bg="pink.500"
                color="white"
                _hover={{ bg: 'pink.600' }}
                onClick={handleSubmit}
                isLoading={createStory.isPending}
                isDisabled={!mediaUrl || isUploading}
                leftIcon={<Upload size={16} />}
                flex={1}
              >
                Share Story
              </Button>
            ) : (
              <Button
                bg="pink.500"
                color="white"
                _hover={{ bg: 'pink.600' }}
                onClick={openFilePicker}
                isLoading={isUploading}
                isDisabled={isUploading}
                leftIcon={<Upload size={16} />}
                flex={1}
              >
                Upload
              </Button>
            )}
          </HStack>
        </ModalFooter>
      </ModalContent>
    </Modal>
  );
}
